/**
 * Vault category catalog.
 *
 * Mirrors the rows seeded in migrations/0001_init.sql. The catalog is the
 * single source of truth for category metadata visible to the client. The
 * server reads from the `categories` D1 table; the client imports this
 * constant. Both must agree.
 *
 * To add or rename a category: edit BOTH this file and write an additive
 * migration that INSERTs the new row. Never UPDATE existing IDs.
 */

import type { CategoryId } from "./index.js";

export interface CategoryDef {
  readonly id: CategoryId;
  readonly slug: string;
  readonly label_en: string;
  readonly label_jp: string;
  readonly icon_name:
    | "mail"
    | "shopping-cart"
    | "map-pin"
    | "globe"
    | "at-sign"
    | "credit-card"
    | "megaphone"
    | "cloud"
    | "ticket"
    | "wifi";
  readonly default_brand_color: `#${string}`;
  readonly order_index: number;
}

export const CATEGORIES: readonly CategoryDef[] = Object.freeze([
  {
    id: "cat_01_google_mail" as CategoryId,
    slug: "google-mail",
    label_en: "Google / Mail Accounts",
    label_jp: "Google・メールアカウント",
    icon_name: "mail",
    default_brand_color: "#1d4ed8",
    order_index: 1,
  },
  {
    id: "cat_02_ota_supplier" as CategoryId,
    slug: "ota-supplier",
    label_en: "OTA / Supplier Portals",
    label_jp: "OTA・サプライヤー",
    icon_name: "shopping-cart",
    default_brand_color: "#0f766e",
    order_index: 2,
  },
  {
    id: "cat_03_reservations" as CategoryId,
    slug: "reservations",
    label_en: "Reservations & Activities",
    label_jp: "予約・アクティビティ",
    icon_name: "map-pin",
    default_brand_color: "#b45309",
    order_index: 3,
  },
  {
    id: "cat_04_website_hosting" as CategoryId,
    slug: "website-hosting",
    label_en: "Website & Hosting",
    label_jp: "ウェブサイト・ホスティング",
    icon_name: "globe",
    default_brand_color: "#4338ca",
    order_index: 4,
  },
  {
    id: "cat_05_domain" as CategoryId,
    slug: "domain-registrar",
    label_en: "Domain & Registrar",
    label_jp: "ドメイン・レジストラ",
    icon_name: "at-sign",
    default_brand_color: "#7c3aed",
    order_index: 5,
  },
  {
    id: "cat_06_payments" as CategoryId,
    slug: "payments",
    label_en: "Payments",
    label_jp: "決済・支払い",
    icon_name: "credit-card",
    default_brand_color: "#ea580c",
    order_index: 6,
  },
  {
    id: "cat_07_marketing" as CategoryId,
    slug: "marketing-social",
    label_en: "Marketing & Social",
    label_jp: "マーケティング・SNS",
    icon_name: "megaphone",
    default_brand_color: "#db2777",
    order_index: 7,
  },
  {
    id: "cat_08_storage" as CategoryId,
    slug: "storage-productivity",
    label_en: "Storage & Productivity",
    label_jp: "ストレージ・業務ツール",
    icon_name: "cloud",
    default_brand_color: "#0891b2",
    order_index: 8,
  },
  {
    id: "cat_09_discount" as CategoryId,
    slug: "discount-promo",
    label_en: "Discount & Promo Codes",
    label_jp: "割引・プロモコード",
    icon_name: "ticket",
    default_brand_color: "#ca8a04",
    order_index: 9,
  },
  {
    id: "cat_10_office_network" as CategoryId,
    slug: "office-network",
    label_en: "Office Network & Logistics",
    label_jp: "オフィスネットワーク・備品",
    icon_name: "wifi",
    default_brand_color: "#0284c7",
    order_index: 10,
  },
] as const satisfies readonly CategoryDef[]);

/**
 * Lookup table. Returns CategoryDef | undefined under `noUncheckedIndexedAccess`.
 * For a throw-on-miss accessor use {@link getCategoryBySlug}.
 */
export const CATEGORY_BY_SLUG: Readonly<Record<string, CategoryDef>> = Object.freeze(
  Object.fromEntries(CATEGORIES.map((c) => [c.slug, c])),
);

/** Throw-on-miss accessor with a helpful error. */
export function getCategoryBySlug(slug: string): CategoryDef {
  const c = CATEGORY_BY_SLUG[slug];
  if (!c) {
    throw new RangeError(
      `getCategoryBySlug: unknown category slug ${JSON.stringify(slug)}`,
    );
  }
  return c;
}
